import { useState, useEffect, createContext, useContext } from 'react'
import { supabase, hasSupabaseEnv } from '../supabaseClient'
import {
  buildSignInPasswordCandidates,
  isInvalidCredentialsError,
  normalizeAuthEmail,
} from './authInputUtils.js'

const AuthContext = createContext({})

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!hasSupabaseEnv || !supabase) {
      setLoading(false)
      return
    }

    // Get initial session
    const getSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        setUser(session?.user ?? null)
      } catch (error) {
        console.error('Error getting session:', error)
      } finally {
        setLoading(false)
      }
    }

    getSession()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setUser(session?.user ?? null)
        setLoading(false)
      }
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  const signUp = async (email, password, displayName) => {
    if (!supabase) {
      return { data: null, error: new Error('Supabase is not configured. Add .env values first.') }
    }

    try {
      const normalizedEmail = normalizeAuthEmail(email)
      if (!normalizedEmail) {
        throw new Error('Please enter a valid email address.')
      }
      const { data, error } = await supabase.auth.signUp({
        email: normalizedEmail,
        password,
        options: {
          data: {
            display_name: displayName
          }
        }
      })

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      console.error('Error signing up:', error)
      return { data: null, error }
    }
  }

  const signIn = async (email, password) => {
    if (!supabase) {
      return { data: null, error: new Error('Supabase is not configured. Add .env values first.') }
    }

    try {
      const normalizedEmail = normalizeAuthEmail(email)
      if (!normalizedEmail) {
        throw new Error('Please enter a valid email address.')
      }
      const passwordCandidates = buildSignInPasswordCandidates(password)
      let lastError = null

      for (const candidate of passwordCandidates) {
        const { data, error } = await supabase.auth.signInWithPassword({
          email: normalizedEmail,
          password: candidate
        })

        if (!error) {
          return { data, error: null }
        }

        lastError = error
        if (!isInvalidCredentialsError(error)) {
          break
        }
      }

      throw lastError || new Error('Authentication failed.')
    } catch (error) {
      console.error('Error signing in:', error)
      return { data: null, error }
    }
  }

  const signOut = async () => {
    if (!supabase) {
      return { error: new Error('Supabase is not configured. Add .env values first.') }
    }

    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      return { error: null }
    } catch (error) {
      console.error('Error signing out:', error)
      return { error }
    }
  }

  const requestPasswordReset = async (email, redirectTo) => {
    if (!supabase) {
      return { error: new Error('Supabase is not configured. Add .env values first.') }
    }

    try {
      const normalizedEmail = normalizeAuthEmail(email)
      if (!normalizedEmail) {
        throw new Error('Please enter a valid email address.')
      }
      const { error } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
        redirectTo
      })
      if (error) throw error
      return { error: null }
    } catch (error) {
      console.error('Error requesting password reset:', error)
      return { error }
    }
  }

  const updatePassword = async (password) => {
    if (!supabase) {
      return { error: new Error('Supabase is not configured. Add .env values first.') }
    }

    try {
      const { data, error } = await supabase.auth.updateUser({ password })
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      console.error('Error updating password:', error)
      return { data: null, error }
    }
  }

  const value = {
    user,
    loading,
    hasSupabaseEnv,
    signUp,
    signIn,
    signOut,
    requestPasswordReset,
    updatePassword
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
